import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router'
import { QueryParamsService } from '@app/services/query-params.service'
import { UtilsApiService } from '@app/services/util-api.service'
import { concatMap, filter, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators'
import { Subject } from 'rxjs'
import { Title } from '@angular/platform-browser'
import { JoinWithPipe } from '@app/pipes'
import { ReturnUrlService } from '@app/services/return-url.service'
import { MoveUserDataToApiService } from '@app/services/move-user-data-to-api.service'
import { UbeService } from '@app/services/ube.service'
import { NewrelicService } from '@app/services/newrelic.service'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  public pageTitle: string
  private destroy$: Subject<void> = new Subject<void>()

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private queryParamsService: QueryParamsService,
    private utilsApiService: UtilsApiService,
    private titleService: Title,
    private returnUrlService: ReturnUrlService,
    private moveUserDataToApiService: MoveUserDataToApiService,
    private ubeService: UbeService,
    private newrelicService: NewrelicService
  ) {
    // Saving query params to the Service
    this.activatedRoute.queryParams
      .pipe(
        tap((queryParams) => this.queryParamsService.setParams(queryParams)),
        takeUntil(this.destroy$)
      )
      .subscribe()

    // Move user data from cookie to UserData API
    this.moveUserDataToApiService.run().pipe(take(1)).subscribe()

    // Function to detect ES module support
    const isESModuleSupported = () => {
      const script = document.createElement('script')
      return 'noModule' in script
    }

    this.newrelicService.log('es_module_support', {
      supported: isESModuleSupported(),
    })
  }

  ngOnInit(): void {
    // Run service to add returnUrl query parameter to sign-in and sign-out links on each NavigationEnd event
    this.returnUrlService.run().pipe(takeUntil(this.destroy$)).subscribe()

    this.router.events
      .pipe(
        filter((event: NavigationEnd) => event instanceof NavigationEnd),
        concatMap((event: RouterEvent) => {
          return this.utilsApiService.logPageView(event.url.split('?')[0])
        }),
        map(() => {
          return this.activatedRoute
        }),
        map((route: ActivatedRoute) => {
          if (route.firstChild?.snapshot?.data) {
            // UBE screen view tracking
            this.ubeService.trackScreenViewByRoute()
          }
          while (route.firstChild) {
            route = route.firstChild
          }
          return route
        }),
        filter((route: ActivatedRoute) => route.outlet === 'primary'),
        mergeMap((route: ActivatedRoute) => route.data),
        map((params) => params.pageTitle),
        tap((pageTitle) => {
          this.pageTitle = new JoinWithPipe().transform(pageTitle, ' - ', 'Ancestry.com')
          this.titleService.setTitle(this.pageTitle)
        }),
        takeUntil(this.destroy$)
      )
      .subscribe()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
